import { useCallback, useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { FeatureKey, BILLING_INTERVAL_OPTIONS } from '@/lib/plans';

// `available` mirrors backend AVAILABLE_FEATURE_KEYS: only those flags have enforcement behind
// them. The rest are placeholders — shown locked, and stripped from a package when it's saved.
const FEATURE_OPTIONS: { key: FeatureKey; label: string; available: boolean }[] = [
  { key: 'integrations', label: 'Integrations', available: false },
  { key: 'analytics_plus', label: 'Advanced analytics', available: true },
  { key: 'analytics_full', label: 'Full analytics suite', available: false },
  { key: 'templates_custom', label: 'Custom templates', available: true },
  { key: 'dedicated_success', label: 'Dedicated success manager', available: false },
  { key: 'api_access', label: 'API access', available: false },
];

const AVAILABLE_FEATURES = new Set<string>(FEATURE_OPTIONS.filter((f) => f.available).map((f) => f.key));

interface Plan {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  price: number | null;
  currency: string;
  interval: string;
  creditLimit: number;
  topUpRate: number | null;
  topUpMax: number | null;
  // Per-message credit rates, charged at send time. null => unpriced (Enterprise).
  whatsappInitCredits?: number | null;
  whatsappNextCredits?: number | null;
  smsCredits?: number | null;
  isPopular: boolean;
  features: string[];
  limits: { contacts?: number | null; teamMembers?: number | null; campaigns?: number | null; templates?: number | null };
  marketingFeatures: string[];
  isActive: boolean;
  isPublic: boolean;
  isCustom: boolean;
  stripePriceId?: string | null;
  sortOrder: number;
}

interface WorkspaceResult {
  id: string;
  name: string;
  planKey: string | null;
  subscriptionStatus: string;
  ownerEmail: string | null;
}

type FormState = {
  id?: string;
  key: string;
  name: string;
  description: string;
  price: string;
  currency: string;
  interval: string;
  creditLimit: string;
  topUpRate: string;
  topUpMax: string;
  whatsappInitCredits: string;
  whatsappNextCredits: string;
  smsCredits: string;
  isPopular: boolean;
  features: string[];
  contacts: string;
  teamMembers: string;
  campaigns: string;
  templates: string;
  marketingFeatures: string;
  isActive: boolean;
  isPublic: boolean;
  isCustom: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  key: '', name: '', description: '', price: '', currency: 'egp', interval: 'month',
  creditLimit: '0', topUpRate: '', topUpMax: '', whatsappInitCredits: '', whatsappNextCredits: '', smsCredits: '', isPopular: false,
  features: [], contacts: '', teamMembers: '', campaigns: '', templates: '',
  marketingFeatures: '', isActive: true, isPublic: true, isCustom: false, sortOrder: '0',
};

function planToForm(p: Plan): FormState {
  return {
    id: p.id,
    key: p.key,
    name: p.name,
    description: p.description || '',
    price: p.price == null ? '' : String(p.price),
    currency: p.currency || 'egp',
    interval: p.interval || 'month',
    creditLimit: String(p.creditLimit ?? 0),
    topUpRate: p.topUpRate == null ? '' : String(p.topUpRate),
    topUpMax: p.topUpMax == null ? '' : String(p.topUpMax),
    whatsappInitCredits: p.whatsappInitCredits == null ? '' : String(p.whatsappInitCredits),
    whatsappNextCredits: p.whatsappNextCredits == null ? '' : String(p.whatsappNextCredits),
    smsCredits: p.smsCredits == null ? '' : String(p.smsCredits),
    isPopular: !!p.isPopular,
    // Drop placeholder flags on load so they render unchecked and the next save clears them.
    features: Array.isArray(p.features) ? p.features.filter((k) => AVAILABLE_FEATURES.has(k)) : [],
    contacts: p.limits?.contacts == null ? '' : String(p.limits.contacts),
    teamMembers: p.limits?.teamMembers == null ? '' : String(p.limits.teamMembers),
    campaigns: p.limits?.campaigns == null ? '' : String(p.limits.campaigns),
    templates: p.limits?.templates == null ? '' : String(p.limits.templates),
    marketingFeatures: (p.marketingFeatures || []).join('\n'),
    isActive: p.isActive,
    isPublic: p.isPublic,
    isCustom: p.isCustom,
    sortOrder: String(p.sortOrder ?? 0),
  };
}

function buildPayload(form: FormState) {
  const limits: Record<string, number | null> = {};
  if (form.contacts !== '') limits.contacts = parseInt(form.contacts, 10);
  if (form.teamMembers !== '') limits.teamMembers = parseInt(form.teamMembers, 10);
  if (form.campaigns !== '') limits.campaigns = parseInt(form.campaigns, 10);
  // Always sent, unlike the three above, and null when blank. `templates` is the one limit with
  // a non-null default (config/plans.js LIMIT_DEFAULTS), so OMITTING the key does not mean
  // "unlimited" — it means "fall back to 3". Sending an explicit null is the only way this form
  // can express unlimited, and without it, saving the enterprise plan unchanged would silently
  // cap it at 3 templates.
  limits.templates = form.templates === '' ? null : parseInt(form.templates, 10);
  return {
    name: form.name,
    description: form.description || null,
    price: form.price === '' ? null : parseInt(form.price, 10),
    currency: form.currency,
    interval: form.interval,
    creditLimit: parseInt(form.creditLimit, 10) || 0,
    topUpRate: form.topUpRate === '' ? null : parseFloat(form.topUpRate),
    topUpMax: form.topUpMax === '' ? null : parseInt(form.topUpMax, 10),
    // Blank => null => "Custom" on the pricing card, and billing falls back to the
    // config/messageCosts.js defaults rather than charging zero.
    whatsappInitCredits: form.whatsappInitCredits.trim() === '' ? null : parseFloat(form.whatsappInitCredits),
    whatsappNextCredits: form.whatsappNextCredits.trim() === '' ? null : parseFloat(form.whatsappNextCredits),
    smsCredits: form.smsCredits.trim() === '' ? null : parseFloat(form.smsCredits),
    isPopular: form.isPopular,
    features: form.features,
    limits,
    marketingFeatures: form.marketingFeatures.split('\n').map((s) => s.trim()).filter(Boolean),
    isActive: form.isActive,
    isPublic: form.isPublic,
    isCustom: form.isCustom,
    sortOrder: parseInt(form.sortOrder, 10) || 0,
  };
}

export default function AdminPlans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Assign-to-workspace flow.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<Plan | null>(null);
  const [wsSearch, setWsSearch] = useState('');
  const [wsResults, setWsResults] = useState<WorkspaceResult[]>([]);
  const [wsSearching, setWsSearching] = useState(false);
  const [selectedWs, setSelectedWs] = useState<WorkspaceResult | null>(null);
  const [setAsWorkspacePlan, setSetAsWorkspacePlan] = useState(true);
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<{ plans: Plan[] }>('/admin/plans')
      .then((r) => setPlans(r.plans || []))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (p: Plan) => { setForm(planToForm(p)); setOpen(true); };

  const toggleFeature = (key: string) => {
    setForm((f) => ({
      ...f,
      features: f.features.includes(key) ? f.features.filter((k) => k !== key) : [...f.features, key],
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = buildPayload(form);
      if (form.id) {
        await apiFetch(`/admin/plans/${form.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        toast.success('Plan updated');
      } else {
        await apiFetch('/admin/plans', { method: 'POST', body: JSON.stringify({ ...payload, key: form.key }) });
        toast.success('Plan created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: Plan) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try {
      await apiFetch(`/admin/plans/${p.id}`, { method: 'DELETE' });
      toast.success('Plan deleted');
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const openAssign = (p: Plan) => {
    setAssignTarget(p);
    setWsSearch('');
    setWsResults([]);
    setSelectedWs(null);
    setSetAsWorkspacePlan(true);
    setAssignOpen(true);
  };

  // Debounced workspace search while the assign dialog is open.
  useEffect(() => {
    if (!assignOpen) return;
    const q = wsSearch.trim();
    setWsSearching(true);
    const handle = setTimeout(() => {
      apiFetch<{ workspaces: WorkspaceResult[] }>(`/admin/workspaces?search=${encodeURIComponent(q)}`)
        .then((r) => setWsResults(r.workspaces || []))
        .catch((e) => toast.error(e instanceof Error ? e.message : 'Workspace search failed'))
        .finally(() => setWsSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [wsSearch, assignOpen]);

  const doAssign = async () => {
    if (!assignTarget || !selectedWs) return;
    try {
      setAssigning(true);
      await apiFetch(`/admin/plans/${assignTarget.id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ workspaceId: selectedWs.id, setAsWorkspacePlan }),
      });
      toast.success(`"${assignTarget.name}" assigned to ${selectedWs.name}`);
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container mx-auto max-w-6xl py-10 px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Subscription packages</h1>
            <p className="text-sm text-muted-foreground">Manage pricing, credit limits, and feature flags.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New package</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{form.id ? 'Edit package' : 'New package'}</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Key</Label>
                    <Input value={form.key} disabled={!!form.id}
                      onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="growth" />
                  </div>
                  <div className="grid gap-2">
                    <Label>Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>Price (blank = custom)</Label>
                    <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Currency</Label>
                    <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Interval</Label>
                    <Select value={form.interval} onValueChange={(value) => setForm({ ...form, interval: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select interval" />
                      </SelectTrigger>
                      <SelectContent>
                        {BILLING_INTERVAL_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>Monthly credits</Label>
                    <Input type="number" value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Top-up rate (per credit)</Label>
                    <Input type="number" value={form.topUpRate} onChange={(e) => setForm({ ...form, topUpRate: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Max top-up credits (blank = no cap)</Label>
                    <Input type="number" value={form.topUpMax} onChange={(e) => setForm({ ...form, topUpMax: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>Contacts limit (blank = unlimited)</Label>
                    <Input type="number" value={form.contacts} onChange={(e) => setForm({ ...form, contacts: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Team members limit (blank = unlimited)</Label>
                    <Input type="number" value={form.teamMembers} onChange={(e) => setForm({ ...form, teamMembers: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Campaigns limit (blank = unlimited)</Label>
                    <Input type="number" value={form.campaigns} onChange={(e) => setForm({ ...form, campaigns: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Templates limit (blank = unlimited)</Label>
                    <Input type="number" value={form.templates} onChange={(e) => setForm({ ...form, templates: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>WhatsApp init (credits/msg)</Label>
                    <Input type="number" step="0.01" min="0" value={form.whatsappInitCredits} placeholder="7.5"
                      onChange={(e) => setForm({ ...form, whatsappInitCredits: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>WhatsApp next (credits/msg)</Label>
                    <Input type="number" step="0.01" min="0" value={form.whatsappNextCredits} placeholder="5.4"
                      onChange={(e) => setForm({ ...form, whatsappNextCredits: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>SMS (credits/msg)</Label>
                    <Input type="number" step="0.01" min="0" value={form.smsCredits} placeholder="0.32"
                      onChange={(e) => setForm({ ...form, smsCredits: e.target.value })} />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Feature flags</Label>
                  <p className="text-xs text-muted-foreground">
                    Greyed-out flags aren't built yet. They can't be turned on, and saving a package removes them.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {FEATURE_OPTIONS.map((f) => (
                      <label key={f.key}
                        className={`flex items-center gap-2 text-sm ${f.available ? '' : 'text-muted-foreground cursor-not-allowed'}`}>
                        <Checkbox checked={form.features.includes(f.key)} disabled={!f.available}
                          onCheckedChange={() => toggleFeature(f.key)} />
                        {f.label}
                        {!f.available && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Coming soon</Badge>}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Marketing bullet points (one per line)</Label>
                  <Textarea value={form.marketingFeatures} rows={4}
                    onChange={(e) => setForm({ ...form, marketingFeatures: e.target.value })} />
                </div>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} /> Active
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={form.isPublic} onCheckedChange={(v) => setForm({ ...form, isPublic: v })} /> Public (pricing page)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={form.isCustom} onCheckedChange={(v) => setForm({ ...form, isCustom: v })} /> Custom / enterprise
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={form.isPopular} onCheckedChange={(v) => setForm({ ...form, isPopular: v })} /> Most popular
                  </label>
                  <div className="grid gap-1">
                    <Label className="text-xs">Sort order</Label>
                    <Input className="w-20" type="number" value={form.sortOrder}
                      onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || !form.key || !form.name}>
                  {saving ? 'Saving…' : 'Save package'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.key}</TableCell>
                    <TableCell>{p.price == null ? 'Custom' : `${p.currency?.toUpperCase()} ${p.price.toLocaleString()}`}</TableCell>
                    <TableCell>{p.creditLimit?.toLocaleString()}</TableCell>
                    <TableCell className="space-x-1">
                      {!p.isActive && <Badge variant="secondary">inactive</Badge>}
                      {p.isCustom && <Badge variant="secondary">custom</Badge>}
                      {p.isPublic && <Badge variant="outline">public</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" title="Assign to workspace" onClick={() => openAssign(p)}><Building2 className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => remove(p)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Assign “{assignTarget?.name}” to a workspace</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <p className="text-sm text-muted-foreground">
                Binds this package to one workspace as a custom (enterprise) plan. The workspace
                still subscribes via Stripe to receive credits and unlock access.
              </p>
              <div className="grid gap-2">
                <Label>Search workspace</Label>
                <Input value={wsSearch} placeholder="Name or domain…"
                  onChange={(e) => setWsSearch(e.target.value)} />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border divide-y">
                {wsSearching ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : wsResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">No workspaces found.</p>
                ) : (
                  wsResults.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => setSelectedWs(w)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selectedWs?.id === w.id ? 'bg-muted' : ''}`}
                    >
                      <div className="font-medium">{w.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {w.ownerEmail || 'no owner'} · {w.subscriptionStatus}{w.planKey ? ` · ${w.planKey}` : ''}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={setAsWorkspacePlan} onCheckedChange={setSetAsWorkspacePlan} />
                Set as the workspace’s current plan key
              </label>
            </div>
            <DialogFooter>
              <Button onClick={doAssign} disabled={assigning || !selectedWs}>
                {assigning ? 'Assigning…' : selectedWs ? `Assign to ${selectedWs.name}` : 'Select a workspace'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
